import './instrument.js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App.tsx';
import { initializeGigya } from './services/gigya.ts';
import { initializeAnalytics } from './services/analytics.ts';
import { initializeTranslations } from './services/translations.ts';

// Enhanced logging for debugging embedding issues
function logEmbeddingDebugInfo() {
  console.log('🔍 [Embedding Debug] Environment check - START');
  console.log('🌐 [Embedding Debug] Current URL:', window.location.href);
  console.log('🏠 [Embedding Debug] Hostname:', window.location.hostname);
  console.log('📍 [Embedding Debug] Pathname:', window.location.pathname);
  console.log('🔗 [Embedding Debug] Referrer:', document.referrer);
  console.log('📄 [Embedding Debug] Document readyState:', document.readyState);
  console.log('⏰ [Embedding Debug] Timestamp:', new Date().toISOString());

  // Check if we're in an iframe (though you mentioned it's direct div embedding)
  const isInIframe = window !== window.top;
  console.log('🖼️ [Embedding Debug] Is in iframe:', isInIframe);

  // For direct div embedding, check container element properties
  const containerElement = document.getElementById('root-food-shelter-love');
  if (containerElement) {
    console.log('📦 [Embedding Debug] Container element found at check time');
    console.log('📐 [Embedding Debug] Container properties:', {
      tagName: containerElement.tagName,
      className: containerElement.className,
      id: containerElement.id,
      offsetWidth: containerElement.offsetWidth,
      offsetHeight: containerElement.offsetHeight,
      parentElement: containerElement.parentElement?.tagName,
    });
  } else {
    console.log('❌ [Embedding Debug] Container element NOT found at check time');
  }

  console.log('🔍 [Embedding Debug] Environment check - END');
}

// DOM readiness check with retry mechanism
function waitForRootElement(retries = 10, delay = 100): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    const targetId = 'root-food-shelter-love';
    console.log(`🎯 [DOM Check] Looking for element with ID: ${targetId}`);

    const checkElement = (attempt: number) => {
      console.log(`🔍 [DOM Check] Attempt ${attempt}/${retries} - Checking for root element...`);

      const rootElement = document.getElementById(targetId);

      if (rootElement) {
        console.log('✅ [DOM Check] Root element found!');
        console.log('📐 [DOM Check] Element dimensions:', {
          width: rootElement.offsetWidth,
          height: rootElement.offsetHeight,
          visible: rootElement.offsetParent !== null,
        });
        resolve(rootElement);
        return;
      }

      if (attempt >= retries) {
        console.error(`❌ [DOM Check] Root element not found after ${retries} attempts`);
        console.log('📋 [DOM Check] Available elements with IDs:');
        const elementsWithIds = document.querySelectorAll('[id]');
        elementsWithIds.forEach((el) => {
          console.log(`  - ${el.id}: ${el.tagName}`);
        });
        reject(new Error(`Root element with ID '${targetId}' not found after ${retries} attempts`));
        return;
      }

      console.log(`⏳ [DOM Check] Element not found, retrying in ${delay}ms...`);
      setTimeout(() => checkElement(attempt + 1), delay);
    };

    // Start checking
    checkElement(1);
  });
}

// Initialize application with proper error handling
async function initializeApp() {
  try {
    console.log('🚀 [App Init] Starting application initialization...');

    // Log embedding debug info
    logEmbeddingDebugInfo();

    // Pre-load translations from Contentstack (before React mounts)
    console.log('🌐 [App Init] Pre-loading translations...');
    await initializeTranslations();

    // Initialize Gigya SDK
    console.log('🔐 [App Init] Initializing Gigya SDK...');
    initializeGigya();

    // Initialize Analytics utilities (GTM already loaded via HTML)
    console.log('📊 [App Init] Setting up analytics utilities...');
    initializeAnalytics();

    // Wait for DOM readiness
    console.log('⏳ [App Init] Waiting for DOM readiness...');
    const rootElement = await waitForRootElement();

    // Create React root and render app
    console.log('⚛️ [App Init] Creating React root and rendering app...');

    // Collapse AEM parent container to remove extra spacing from host page grid elements (e.g. top-nav-desktop)
    const parentContainer = rootElement.closest('.cmp-container');
    if (parentContainer instanceof HTMLElement) {
      parentContainer.style.display = 'inline';
    }

    // Add class to indicate React has loaded (for any future transition needs)
    rootElement.classList.add('fsl-react-loaded');

    const root = createRoot(rootElement, {
      // Callback called when an error is thrown and not caught by an ErrorBoundary.
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
      }),
      // Callback called when React catches an error in an ErrorBoundary.
      onCaughtError: Sentry.reactErrorHandler(),
      // Callback called when React automatically recovers from errors.
      onRecoverableError: Sentry.reactErrorHandler(),
    });
    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );

    console.log('✅ [App Init] Application initialized successfully!');
  } catch (error) {
    console.error('❌ [App Init] Failed to initialize application:', error);

    // Try to show error message to user if possible
    const fallbackContainer = document.body;
    if (fallbackContainer) {
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #f8d7da;
        color: #721c24;
        padding: 20px;
        border: 1px solid #f5c6cb;
        border-radius: 8px;
        font-family: Arial, sans-serif;
        z-index: 9999;
        max-width: 400px;
        text-align: center;
      `;
      errorDiv.innerHTML = `
        <h3>Food Shelter Love - Initialization Error</h3>
        <p>The application failed to initialize properly.</p>
        <p><small>Please check the console for more details.</small></p>
        <button onclick="window.location.reload()" style="
          background: #721c24;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          margin-top: 10px;
        ">Retry</button>
      `;
      fallbackContainer.appendChild(errorDiv);
    }
  }
}

// Start initialization when DOM is ready
if (document.readyState === 'loading') {
  console.log('📄 [App Init] Document still loading, waiting for DOMContentLoaded...');
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  console.log('📄 [App Init] Document already loaded, starting initialization immediately...');
  initializeApp();
}
